import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Progress, Tooltip, Skeleton } from "antd";
import ReactHighcharts from "react-highcharts";
import { family_img } from "../../assets/images";
import { formatAmount } from "../../constants/overview";
import { RoundedSelect } from "../tailwind/atoms";

const relationshipColors = {
  self: "#1f77b4",
  spouse: "#ff7f0e",
  children: "#2ca02c",
  child: "#2ca02c",
  parents: "#d62728",
  mother: "#d62728",
  father: "#9467bd",
  sibling: "#8c564b",
  mother_in_law: "#e377c2",
  father_in_law: "#7f7f7f",
  grand_mother: "#bcbd22",
  grand_father: "#17becf",
  other: "#aec7e8",
  unknown: "#c7c7c7"
};

const defaultColors = [
  "#2168f3", "#48bb78", "#f6ad55", "#9f7aea", "#ed64a6", "#f56565",
  "#4299e1", "#38b2ac", "#ecc94b", "#805ad5"
];

const MAX_VISIBLE_ITEMS = 8;

const RelationshipSplit = ({ data, loading = false, servicesMap = {}, currentService, handleServiceChange = () => {} }) => {
  const [metric, setMetric] = useState("amount");
  const [donutOptions, setDonutOptions] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [listMaxHeight, setListMaxHeight] = useState("0px");
  const listWrapperRef = useRef(null);

  const numberFormatter = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0
  });

  useEffect(() => {
    if (!data) return;
    const categories = Object.keys(data);
    const total = categories.reduce((sum, cat) => sum + data[cat][metric], 0);

    const chartData = categories.map((cat) => ({
      name: cat.charAt(0).toUpperCase() + cat.slice(1),
      y: data[cat][metric],
      color:
        relationshipColors[cat.split("/")[0].toLowerCase()] ||
        defaultColors[categories.indexOf(cat) % defaultColors.length],
      percentage: total
        ? parseFloat(((data[cat][metric] / total) * 100).toFixed(1))
        : 0
    }));

    setDonutOptions({
      chart: { type: "pie" },
      title: { text: "" },
      credits: { enabled: false },
      tooltip: {
        pointFormatter: function () {
          const val =
            metric === "amount"
              ? `₹${numberFormatter.format(this.y)}`
              : numberFormatter.format(this.y);
          return `<b>${this.name}:</b> ${val} (${this.percentage.toFixed(1)}%)`;
        }
      },
      plotOptions: {
        pie: {
          innerSize: "60%",
          dataLabels: {
            enabled: true,
            formatter: function () {
              const val =
                metric === "amount"
                  ? `₹${formatAmount(this.y)}`
                  : numberFormatter.format(this.y);
              return `${this.point.name}: ${val}`;
            },
            style: { fontSize: "11px" }
          }
        }
      },
      series: [{ name: metric, data: chartData }]
    });
  }, [metric, data]);

  useEffect(() => {
    const wrapper = listWrapperRef.current;
    if (!wrapper) return;

    if (expanded) {
      const fullHeight = wrapper.scrollHeight;
      setListMaxHeight(`${fullHeight}px`);
      const t = setTimeout(() => setListMaxHeight("none"), 350);
      return () => clearTimeout(t);
    }

    const firstChild = wrapper.querySelector(".relation-row");
    const rowHeight = firstChild ? firstChild.getBoundingClientRect().height : 56;
    const collapseHeight = Math.ceil(rowHeight * MAX_VISIBLE_ITEMS);
    setListMaxHeight(`${collapseHeight}px`);
  }, [expanded, data]);

  if (loading) {
    return (
      <Card>
        <h1 className="text-base text-gray-700 mb-8 leading-none flex gap-4">
          <img src={family_img} className="h-6 w-6" alt="" />
          <span className="font-fig-semibold">Relationship-wise Split</span>
        </h1>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  if (!data) {
    return (
      <Card title="Relationship-wise Split">
        <p>No data available</p>
      </Card>
    );
  }

  const categories = Object.keys(data);
  const totalCount = categories.reduce((sum, cat) => sum + data[cat].volume, 0);
  const totalAmount = categories.reduce((sum, cat) => sum + data[cat].amount, 0);

  return (
    <Card>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-base text-gray-700 mb-8 leading-none flex gap-4">
          <img src={family_img} className="h-6 w-6" alt="" />
          <span className="font-fig-semibold">Relationship-wise Split</span>
        </h1>
        
        <div className="inline-flex mb-4 bg-gray-100 rounded-md p-1 space-x-1 overflow-x-auto">
          {["volume", "amount"].map((type) => {
            const isActive = metric === type;
            return (
              <button
                key={type}
                onClick={() => setMetric(type)}
                className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors duration-200 focus:outline-none ${
                  isActive
                    ? "bg-white text-black shadow-sm rounded-md"
                    : "text-gray-500"
                }`}
              >
                {type === "volume" ? "Count" : "Value (₹)"}
              </button>
            );
          })}
        </div>

        <RoundedSelect
          listClasses="w-34"
          classes="ml-8 -mt-2"
          selectOptions={Object.values(servicesMap)}
          selectValues={Object.keys(servicesMap).map((_, i) => i)}
          curvalue={servicesMap[currentService] || 'Overall'}
          selectHandler={(index) =>
            handleServiceChange('relationship_split', Object.keys(servicesMap)[index])
          }
        />
      </div>

      <Row gutter={16}>
        {/* Donut Chart */}
        <Col span={12}>
          {donutOptions && <ReactHighcharts config={donutOptions} />}
        </Col>

        <Col span={12}>
          {/* Header Row */}
          <div className="flex justify-between items-center mb-4 px-2 sm:px-4">
            <div className="w-1/4" />
            <div className="w-2/4" />
            <div className="flex justify-end w-1/4 gap-6 text-xs sm:text-sm text-gray-500 font-fig-normal tracking-wide">
              <span>Share</span>
              <span>AOV</span>
            </div>
          </div>

          {/* Scroll Wrapper */}
          <div
            ref={listWrapperRef}
            style={{
              maxHeight: listMaxHeight === "none" ? "none" : listMaxHeight,
              overflow: "hidden",
              transition: "max-height 320ms ease, opacity 200ms ease"
            }}
          >
            {categories.map((cat, idx) => {
              const value = data[cat][metric];
              const aov = data[cat].amount / (data[cat].volume || 1);
              const percent =
                metric === "volume"
                  ? parseFloat(((value / totalCount) * 100).toFixed(1))
                  : parseFloat(((value / totalAmount) * 100).toFixed(1));
              const color =
                relationshipColors[cat.toLowerCase().replace(/\s+/g, "_")] ||
                defaultColors[idx % defaultColors.length];

              return (
                <div
                  key={cat}
                  className="relation-row flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 my-1 transition-all hover:scale-[1.01] hover:bg-gray-50 rounded-xl p-2 sm:p-3"
                >
                  {/* Category + Dot */}
                  <div className="flex items-center gap-3 w-full sm:w-1/4">
                    <div
                      className="w-3 h-3 rounded-full shadow-sm flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <Tooltip title={cat.charAt(0).toUpperCase() + cat.slice(1)}>
                      <span className="truncate font-fig-normal text-gray-800 text-sm">
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </span>
                    </Tooltip>
                  </div>

                  {/* Progress Bar */}
                  <Tooltip
                    title={`${cat.charAt(0).toUpperCase() + cat.slice(1)}: ${
                      metric === "amount"
                        ? `₹${numberFormatter.format(value)}`
                        : numberFormatter.format(value)
                    } (${percent}%)`}
                    placement="topLeft"
                  >
                    <Progress
                      percent={percent}
                      showInfo={false}
                      strokeColor={color}
                      trailColor="#f0f0f0"
                      strokeWidth={8}
                      className="w-full sm:w-2/4 cursor-pointer"
                    />
                  </Tooltip>

                  {/* Share + AOV */}
                  <div className="flex justify-end gap-6 w-full sm:w-1/4 text-sm sm:text-base font-fig-semibold text-gray-800 tabular-nums">
                    <span className="text-blue-600 min-w-[3.5rem] text-right">
                      {percent}%
                    </span>
                    <span className="text-green-600 min-w-[5rem] text-right">
                      ₹{numberFormatter.format(aov)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expand/Collapse Button */}
          {categories.length > MAX_VISIBLE_ITEMS && (
            <div className="flex justify-center mt-4">
              <button
                onClick={() => setExpanded((s) => !s)}
                className="px-5 py-2 text-sm text-blue-600 font-fig-semibold rounded-full hover:bg-blue-50 transition-all duration-200 flex items-center gap-2 shadow-sm border border-blue-100"
                aria-expanded={expanded}
              >
                <span>
                  {expanded
                    ? "Show Less"
                    : `Show More (${categories.length - MAX_VISIBLE_ITEMS})`}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 transform transition-transform duration-200 ${
                    expanded ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Totals Card */}
          <div className="mt-4 shadow-sm rounded-xl p-4 border border-gray-100">
            <div className="flex flex-col sm:flex-row items-center justify-between text-sm sm:text-base gap-6 sm:gap-0">
              <div className="flex flex-col items-center sm:items-start">
                <span className="text-gray-500 font-fig-normal">Total Claims</span>
                <span className="text-base sm:text-lg font-fig-semibold text-blue-600">
                  {numberFormatter.format(totalCount)}
                </span>
              </div>
              <div className="hidden sm:block h-10 w-px bg-gray-200 mx-4" />
              <div className="flex flex-col items-center sm:items-end">
                <span className="text-gray-500 font-fig-normal">Total Value</span>
                <span className="text-base sm:text-lg font-fig-semibold text-green-600">
                  ₹{numberFormatter.format(totalAmount)}
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default RelationshipSplit;
